/* =========================================================
   Character Select — theme.js
   One IIFE. Feature blocks register into a single rAF loop.
   Everything is idempotent so the Shopify editor can re-init sections.
   ========================================================= */
(() => {
  'use strict';

  const S = window.CS || { routes: {}, strings: {} };
  const html = document.documentElement;
  const body = document.body;
  const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const DESKTOP = () => matchMedia('(min-width: 990px)').matches;
  // theme settings are exposed as data-motion-* on <body> (namespaced so they never collide with element hooks such as [data-parallax])
  const flag = (k) => body.dataset['motion' + k.charAt(0).toUpperCase() + k.slice(1)] !== 'false';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const once = (el, key) => { if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; };
  const EASE_OUT = (p) => 1 - Math.pow(1 - p, 4);

  /* ---------- money ---------- */
  function money(cents) {
    const fmt = S.moneyFormat || '${{amount}}';
    const n = (cents || 0) / 100;
    const fixed = (v, d, thou, dec) => {
      const parts = v.toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.length > 1 ? parts.join(dec) : parts[0];
    };
    return fmt
      .replace('{{amount_no_decimals_with_comma_separator}}', fixed(n, 0, '.', ','))
      .replace('{{amount_with_comma_separator}}', fixed(n, 2, '.', ','))
      .replace('{{amount_with_apostrophe_separator}}', fixed(n, 2, "'", '.'))
      .replace('{{amount_with_space_separator}}', fixed(n, 2, ' ', ','))
      .replace('{{amount_no_decimals_with_space_separator}}', fixed(n, 0, ' ', ','))
      .replace('{{amount_no_decimals}}', fixed(n, 0, ',', '.'))
      .replace('{{amount}}', fixed(n, 2, ',', '.'));
  }

  /* ---------- toast ---------- */
  let toastT;
  function toast(msg) {
    const el = $('.toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-on');
    clearTimeout(toastT);
    toastT = setTimeout(() => el.classList.remove('is-on'), 2600);
  }

  /* ---------- split text ---------- */
  function split(el) {
    if (!once(el, 'splitDone')) return;
    let i = 0;
    const d0 = parseInt(el.dataset.splitDelay || '0', 10);
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const words = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          words.forEach((w) => {
            if (!w) return;
            if (/^\s+$/.test(w)) { frag.appendChild(document.createTextNode(' ')); return; }
            const outer = document.createElement('span');
            outer.className = 'w';
            const inner = document.createElement('span');
            inner.textContent = w;
            inner.style.setProperty('--i', i++);
            if (d0) inner.style.setProperty('--d0', d0 + 'ms');
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && !child.classList.contains('w')) {
          walk(child);
        }
      });
    };
    walk(el);
  }

  /* ---------- count-up ---------- */
  function countUp(el) {
    if (!once(el, 'counted')) return;
    const to = parseFloat(el.dataset.count || '0');
    const dec = parseInt(el.dataset.decimals || '0', 10);
    if (RM || !flag('reveals')) { el.textContent = to.toFixed(dec); return; }
    const t0 = performance.now();
    const dur = 1900;
    const step = (t) => {
      const p = clamp((t - t0) / dur, 0, 1);
      el.textContent = (to * EASE_OUT(p)).toFixed(dec);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---------- reveals ---------- */
  const revealsOn = flag('reveals') && !RM;
  if (!revealsOn) html.classList.add('no-reveal');
  const io = revealsOn ? new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('in');
      if (e.target.hasAttribute('data-count')) countUp(e.target);
      io.unobserve(e.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }) : null;

  function observe(root = document) {
    $$('.rv, .clip, [data-split], [data-count], .edition', root).forEach((el) => {
      if (el.matches('[data-split]')) split(el);
      if (!revealsOn) { el.classList.add('in'); if (el.hasAttribute('data-count')) countUp(el); return; }
      if (el.classList.contains('in')) return;
      io.observe(el);
    });
  }

  /* =========================================================
     Frame loop registry
     ========================================================= */
  const loops = [];
  let y = window.scrollY, lastY = y, vel = 0, W = innerWidth, H = innerHeight;
  function frame(t) {
    y = window.scrollY;
    vel = lerp(vel, y - lastY, 0.12);
    lastY = y;
    for (let i = 0; i < loops.length; i++) loops[i](t);
    requestAnimationFrame(frame);
  }
  addEventListener('resize', () => { W = innerWidth; H = innerHeight; resizers.forEach((f) => f()); }, { passive: true });
  addEventListener('orientationchange', () => setTimeout(() => { W = innerWidth; H = innerHeight; resizers.forEach((f) => f()); }, 300), { passive: true });
  const resizers = [];

  /* ---------- smooth (inertia) scroll ---------- */
  let smooth = false;
  if (flag('smooth') && FINE && !RM && !S.designMode) {
    smooth = true;
    let sT = window.scrollY, sC = sT, lastSelf = 0, active = false;
    const max = () => document.documentElement.scrollHeight - innerHeight;
    addEventListener('wheel', (e) => {
      if (body.classList.contains('is-locked') || e.ctrlKey) return;
      const scrollable = e.target.closest && e.target.closest('[data-native-scroll], .pdp__thumbs, textarea, select');
      // a sideways rail only keeps sideways swipes; an up/down wheel over it still scrolls the page (no stuck feeling)
      if (scrollable && (Math.abs(e.deltaX) > Math.abs(e.deltaY) || scrollable.scrollHeight > scrollable.clientHeight + 1)) return;
      e.preventDefault();
      const side = Math.abs(e.deltaX) > Math.abs(e.deltaY) && e.target.closest && e.target.closest('.wild.is-pinned');
      const raw = side ? e.deltaX : e.deltaY;
      const d = e.deltaMode === 1 ? raw * 16 : e.deltaMode === 2 ? raw * innerHeight : raw;
      sT = clamp(sT + d, 0, max());
      active = true;
    }, { passive: false });
    addEventListener('scroll', () => {
      if (performance.now() - lastSelf > 80) { sT = sC = window.scrollY; active = false; }
    }, { passive: true });
    loops.push(() => {
      if (!active) return;
      const diff = sT - sC;
      if (Math.abs(diff) < 0.35) { sC = sT; active = false; return; }
      sC += diff * 0.095;
      lastSelf = performance.now();
      window.scrollTo(0, sC);
    });
    window.CS.scrollTo = (top) => { sT = clamp(top, 0, max()); active = true; };
  }
  function scrollToEl(el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 20;
    if (smooth && window.CS.scrollTo) window.CS.scrollTo(top);
    else window.scrollTo({ top, behavior: RM ? 'auto' : 'smooth' });
  }

  /* ---------- header ---------- */
  function initHeader() {
    const hdr = $('#SiteHeader');
    if (!hdr || !once(hdr, 'init')) return;
    let hidden = false, prev = window.scrollY;
    loops.push(() => {
      const dy = y - prev; prev = y;
      hdr.classList.toggle('is-scrolled', y > 40);
      if (hdr.dataset.sticky === 'false') return;
      if (y > 140 && dy > 2 && !hidden && !body.classList.contains('is-locked')) { hidden = true; hdr.classList.add('is-hidden'); }
      else if ((dy < -2 || y < 140) && hidden) { hidden = false; hdr.classList.remove('is-hidden'); }
    });
  }

  /* ---------- announcement ticker speed ---------- */
  function initAnnouncement(root) {
    $$('.ann', root).forEach((el) => { el.style.setProperty('--ann-speed', (el.dataset.speed || 40) + 's'); });
  }

  /* ---------- marquee (velocity driven) ---------- */
  function initMarquee(root) {
    $$('[data-marquee]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const track = $('.marq__track', el);
      const group = $('.marq__group', el);
      if (!track || !group) return;
      let x = 0, w = group.offsetWidth;
      resizers.push(() => { w = group.offsetWidth; });
      const dir = el.dataset.direction === 'right' ? 1 : -1;
      const base = w / (parseFloat(el.dataset.speed || 60)) / 60; // px per frame at rest
      if (RM) return;
      loops.push(() => {
        const speed = base + Math.min(Math.abs(vel) * 0.35, 18);
        x += dir * speed;
        if (dir < 0 && x <= -w) x += w;
        if (dir > 0 && x >= 0) x -= w;
        track.style.transform = `translate3d(${x}px,0,0)`;
      });
    });
  }

  /* ---------- parallax ---------- */
  function initParallax(root) {
    if (!flag('parallax') || RM) return;
    $$('[data-parallax]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const k = parseFloat(el.dataset.parallax || '0.1');
      // full-bleed backgrounds are clipped by their banner; anything else would drift onto the text below it once the layout stacks on phones
      const clipped = el.matches('.cban__bg, .banner__media');
      let top = 0, h = 0;
      const measure = () => { const r = el.getBoundingClientRect(); top = r.top + window.scrollY; h = r.height; };
      measure(); resizers.push(measure);
      loops.push(() => {
        if (!clipped && W < 990) { if (el.style.transform) el.style.transform = ''; return; }
        if (y + H < top || y > top + h) return;
        const p = (y + H / 2 - (top + h / 2)) * k;
        el.style.transform = `translate3d(0,${p.toFixed(1)}px,0)`;
      });
    });
  }





  /* ---------- pointer: cursor, magnetic, tilt ---------- */
  function initPointer() {
    if (!FINE || RM) return;
    const cur = $('.cursor');
    let mx = W / 2, my = H / 2, rx = mx, ry = my;
    addEventListener('mousemove', (e) => { mx = e.clientX; my = e.clientY; }, { passive: true });
    if (cur) {
      const dot = $('.cursor__dot', cur), ring = $('.cursor__ring', cur), label = $('.cursor__label', cur);
      document.addEventListener('mouseleave', () => cur.classList.add('is-hidden'));
      document.addEventListener('mouseenter', () => cur.classList.remove('is-hidden'));
      addEventListener('mousedown', () => cur.classList.add('is-down'));
      addEventListener('mouseup', () => cur.classList.remove('is-down'));
      document.addEventListener('mouseover', (e) => {
        const lab = e.target.closest('[data-cursor]');
        if (lab) { label.textContent = lab.dataset.cursor; cur.classList.add('is-label'); cur.classList.remove('is-link'); return; }
        cur.classList.remove('is-label');
        cur.classList.toggle('is-link', !!e.target.closest('a, button, label, summary, select, input'));
      });
      loops.push(() => {
        rx = lerp(rx, mx, 0.18); ry = lerp(ry, my, 0.18);
        dot.style.transform = `translate3d(${mx}px,${my}px,0) translate(-50%,-50%)`;
        ring.style.transform = `translate3d(${rx}px,${ry}px,0) translate(-50%,-50%)`;
      });
    }
    // magnetic buttons
    const mags = new Set();
    const collectMags = (root = document) => $$('.mag', root).forEach((m) => mags.add(m));
    collectMags();
    window.CS.collectMags = collectMags;
    loops.push(() => {
      mags.forEach((m) => {
        const r = m.getBoundingClientRect();
        if (r.bottom < 0 || r.top > H) return;
        const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        const dx = mx - cx, dy = my - cy, dist = Math.hypot(dx, dy);
        if (dist < 120) m.style.transform = `translate3d(${(dx * 0.22).toFixed(1)}px,${(dy * 0.22).toFixed(1)}px,0)`;
        else if (m.style.transform) m.style.transform = '';
      });
    });
    // tilt cards
    document.addEventListener('mousemove', (e) => {
      const t = e.target.closest('.tilt');
      if (!t) return;
      const r = t.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
      t.style.transform = `perspective(1000px) rotateX(${(-py * 5).toFixed(2)}deg) rotateY(${(px * 6).toFixed(2)}deg)`;
    }, { passive: true });
    document.addEventListener('mouseout', (e) => { const t = e.target.closest('.tilt'); if (t && !t.contains(e.relatedTarget)) t.style.transform = ''; }, { passive: true });
  }

  /* =========================================================
     Overlays: menu / search / cart drawer
     ========================================================= */
  let lastFocus = null;
  function lock() { body.classList.add('is-locked'); }
  function unlock() { if (!$('.is-open.mm, .is-open.drawer, .is-open.psearch')) body.classList.remove('is-locked'); }
  function openOverlay(el, focusSel) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); lock();
    const f = focusSel ? $(focusSel, el) : $('.icon-btn, button, a, input', el);
    setTimeout(() => f && f.focus({ preventScroll: true }), 250);
  }
  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); unlock();
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('.mm.is-open, .drawer.is-open, .psearch.is-open').forEach(closeOverlay);
  });

  /* ---------- cart ---------- */
  const cartType = S.cartType || 'drawer';
  const drawer = () => $('#CartDrawer');
  function openCart() { const d = drawer(); if (d) openOverlay(d, '.icon-btn'); else location.href = S.routes.cart || '/cart'; }
  async function renderSections(ids) {
    const url = `${(S.routes.root || '/').replace(/\/$/, '')}/?sections=${ids.join(',')}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    return res.json();
  }
  function applySections(sections) {
    if (!sections) return;
    const parser = new DOMParser();
    if (sections['cart-drawer'] && drawer()) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      const cur = drawer();
      if (fresh && cur) { cur.innerHTML = fresh.innerHTML; cur.className = fresh.className + (cur.classList.contains('is-open') ? ' is-open' : ''); initTiles(cur); }
    }
    if (sections['cart-icon-bubble']) {
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const b = $('#CartBubble');
      const inner = doc.querySelector('.shopify-section') || doc.body;
      if (b) b.innerHTML = inner.innerHTML;
    }
    const page = $('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const doc = parser.parseFromString(sections[page.dataset.sectionId], 'text/html');
      const fresh = doc.querySelector('[data-cart-page]');
      if (fresh) { page.innerHTML = fresh.innerHTML; observe(page); }
    }
  }
  function cartSectionIds() {
    const ids = ['cart-icon-bubble'];
    if (drawer()) ids.push('cart-drawer');
    const page = $('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  }
  async function changeLine(line, quantity, lineEl) {
    if (lineEl) lineEl.classList.add('is-loading');
    try {
      const res = await fetch(`${S.routes.cartChange || '/cart/change'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: location.pathname })
      });
      const data = await res.json();
      if (data.errors || data.description) throw new Error(data.errors || data.description);
      applySections(data.sections);
    } catch (err) {
      if (lineEl) { lineEl.classList.remove('is-loading'); const e = $('.line__error', lineEl); if (e) { e.textContent = err.message || S.strings.cartError; e.hidden = false; } }
      else toast(S.strings.cartError);
    }
  }
  let noteT;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteT);
    noteT = setTimeout(() => fetch(`${S.routes.cartUpdate || '/cart/update'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) }), 500);
  });

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.bound) return;
      this.bound = true;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      if (cartType !== 'drawer' && !drawer()) return; // native submit → cart page
      e.preventDefault();
      const btn = (e.submitter && this.form.contains(e.submitter)) ? e.submitter : this.form.querySelector('[type="submit"]');
      const err = this.querySelector('.form__error');
      if (btn && btn.hasAttribute('disabled')) return;
      btn && btn.classList.add('is-loading');
      if (err) err.hidden = true;
      const fd = new FormData(this.form);
      if (btn && btn.dataset.variant) fd.set('id', btn.dataset.variant);
      const mirror = this.form.querySelector('[data-quantity-mirror]');
      const qtyInput = this.closest('.pdp__info, .pdp') ? $('.pdp__qty .qty__input', this.closest('.pdp__info, .pdp')) : null;
      if (mirror && qtyInput) fd.set('quantity', qtyInput.value || 1);
      fd.append('sections', cartSectionIds().join(','));
      fd.append('sections_url', location.pathname);
      try {
        const res = await fetch(`${S.routes.cartAdd || '/cart/add'}`, { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' } });
        const data = await res.json();
        if (data.status || data.errors) throw new Error(data.description || data.message || S.strings.cartError);
        applySections(data.sections);
        openCart();
        toast(S.strings.added || 'Added');
      } catch (ex) {
        if (err) { err.textContent = ex.message; err.hidden = false; } else toast(ex.message);
      } finally { btn && btn.classList.remove('is-loading'); }
    }
  }
  customElements.get('product-form') || customElements.define('product-form', ProductForm);

  class CartDrawer extends HTMLElement { connectedCallback() { /* markup only; behaviour is delegated */ } }
  customElements.get('cart-drawer') || customElements.define('cart-drawer', CartDrawer);

  /* ---------- bundle box (product page): 2 shirts −10%, 3 shirts −15%, mixed countries and sizes ----------
     The discounts themselves are Shopify automatic discounts; this puts the shirts in the bag together.
     This shirt uses the size chosen above; every extra shirt gets its own size. */
  class BundleBox extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      this.n = 1; this.picks = []; // [{ opt, size }]
      this.section = this.closest('[data-product]') || document;
      this.addEventListener('click', (e) => {
        const tier = e.target.closest('[data-tier]');
        if (tier) { this.setTier(parseInt(tier.dataset.tier, 10)); return; }
        const opt = e.target.closest('[data-opt]');
        if (opt && !opt.disabled) { this.toggle(opt); return; }
        const sz = e.target.closest('[data-pick-size]');
        if (sz && !sz.disabled) { const p = this.picks[parseInt(sz.dataset.i, 10)]; if (p) { p.size = sz.dataset.pickSize; this.say(''); this.render(); } return; }
        const un = e.target.closest('[data-unpick]');
        if (un) { this.picks.splice(parseInt(un.dataset.i, 10), 1); this.say(''); this.render(); return; }
        if (e.target.closest('[data-bundle-add]')) this.add();
      });
      this.section.addEventListener('change', () => this.render());
      this.render();
    }
    t(key, vars = {}) { let s = this.dataset['t' + key] || ''; Object.keys(vars).forEach((k) => { s = s.split('[' + k + ']').join(vars[k]); }); return s; }
    current() {
      const idEl = $('[data-type="add-to-cart-form"] [name="id"]', this.section);
      const vsEl = $('[data-variants]', this.section);
      let variants = []; try { variants = JSON.parse(vsEl.textContent); } catch (e) { /* none */ }
      const v = variants.find((x) => String(x.id) === String(idEl && idEl.value));
      const i = parseInt(this.dataset.sizeIndex || '0', 10);
      return { id: idEl ? idEl.value : null, size: v && v.options ? v.options[i] : null, price: v ? v.price : parseInt(this.dataset.price, 10) };
    }
    sizes(opt) { let map = {}; try { map = JSON.parse(opt.dataset.sizes); } catch (e) { /* none */ } return map; }
    defaultSize(opt) {
      const map = this.sizes(opt), want = this.current().size;
      if (want && map[want] && map[want][1]) return want;
      return Object.keys(map).find((k) => map[k][1]) || want;
    }
    setTier(n) { this.n = n; this.picks = this.picks.slice(0, Math.max(0, n - 1)); this.say(''); this.render(); }
    toggle(opt) {
      const at = this.picks.findIndex((p) => p.opt === opt);
      if (at > -1) this.picks.splice(at, 1);
      else { this.picks.push({ opt, size: this.defaultSize(opt) }); while (this.picks.length > this.n - 1) this.picks.shift(); }
      this.say(''); this.render();
    }
    say(text) { const m = $('[data-msg]', this); if (m) { m.textContent = text; m.hidden = !text; } }
    el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
    renderList(cur) {
      const list = $('[data-picked]', this);
      if (!list) return;
      list.textContent = '';
      if (this.n < 2 || !this.picks.length) { list.hidden = true; return; }
      list.hidden = false;
      const mine = this.el('div', 'pdp__bundle-row is-mine');
      mine.append(this.el('span', 'pdp__bundle-row-name', this.dataset.self || ''), this.el('span', 'pdp__bundle-row-note', cur.size ? this.t('Yours', { size: cur.size }) : ''));
      list.append(mine);
      this.picks.forEach((p, i) => {
        const name = p.opt.dataset.name, map = this.sizes(p.opt);
        const row = this.el('div', 'pdp__bundle-row');
        const sizes = this.el('div', 'pdp__bundle-row-sizes');
        sizes.setAttribute('role', 'radiogroup'); sizes.setAttribute('aria-label', this.t('SizeFor', { name }));
        Object.keys(map).forEach((size) => {
          const b = this.el('button', 'pdp__bundle-sz' + (size === p.size ? ' is-on' : ''), size);
          b.type = 'button'; b.dataset.pickSize = size; b.dataset.i = i;
          b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', size === p.size ? 'true' : 'false');
          if (!map[size][1]) { b.disabled = true; b.classList.add('is-out'); }
          sizes.append(b);
        });
        const x = this.el('button', 'pdp__bundle-x');
        x.type = 'button'; x.dataset.unpick = ''; x.dataset.i = i; x.setAttribute('aria-label', this.t('Remove', { name })); x.innerHTML = '&times;';
        row.append(this.el('span', 'pdp__bundle-row-name', name), sizes, x);
        list.append(row);
      });
    }
    render() {
      const cur = this.current();
      $$('[data-tier]', this).forEach((b) => { const on = parseInt(b.dataset.tier, 10) === this.n; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
      const pick = $('[data-pick]', this), btn = $('[data-bundle-add]', this);
      const open = this.n > 1;
      if (pick) pick.hidden = !open;
      if (btn) btn.hidden = !open;
      if (!open) { this.renderList(cur); return; }
      const need = this.n - 1 - this.picks.length;
      const ask = $('[data-ask]', this);
      if (ask) ask.textContent = need <= 0 ? this.t('Ready') : need === 1 ? this.t('PickOne') : this.t('PickMany', { n: need });
      $$('[data-opt]', this).forEach((o) => {
        const on = this.picks.some((p) => p.opt === o);
        o.classList.toggle('is-on', on); o.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.renderList(cur);
      const total = Math.round(cur.price * this.n * (this.n >= 3 ? 0.85 : 0.9));
      if (btn) {
        btn.disabled = need > 0;
        $('[data-add-label]', btn).textContent = need > 0
          ? (need === 1 ? this.t('PickOne') : this.t('PickMany', { n: need }))
          : this.t('Add', { n: this.n }) + ' · ' + money(total);
      }
    }
    async add() {
      const cur = this.current();
      if (!cur.id || this.picks.length < this.n - 1) return;
      const items = [{ id: Number(cur.id), quantity: 1 }];
      for (const p of this.picks) {
        const hit = this.sizes(p.opt)[p.size];
        if (!hit || !hit[1]) { this.say(this.t('Out', { name: p.opt.dataset.name, size: p.size })); return; }
        items.push({ id: hit[0], quantity: 1 });
      }
      const btn = $('[data-bundle-add]', this);
      btn.classList.add('is-loading'); this.say('');
      try {
        const res = await fetch(`${S.routes.cartAdd || '/cart/add'}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: cartSectionIds(), sections_url: location.pathname })
        });
        const data = await res.json();
        if (data.status || data.errors) throw new Error(data.description || data.message || S.strings.cartError);
        if (!drawer()) { location.href = S.routes.cart || '/cart'; return; }
        applySections(data.sections);
        openCart();
        toast(this.t('Added') || S.strings.added);
        this.picks = []; this.render();
      } catch (ex) {
        this.say(ex.message || S.strings.cartError);
      } finally { btn.classList.remove('is-loading'); }
    }
  }
  customElements.get('bundle-box') || customElements.define('bundle-box', BundleBox);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const v = parseInt(this.input.value || '0', 10) + (b.name === 'plus' ? 1 : -1);
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        this.input.value = clamp(v, min, max);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.get('quantity-input') || customElements.define('quantity-input', QuantityInput);

  document.addEventListener('change', (e) => {
    const inp = e.target;
    if (inp.matches('.qty__input[data-line]')) changeLine(parseInt(inp.dataset.line, 10), parseInt(inp.value || '0', 10), inp.closest('.line'));
    if (inp.matches('[data-localization-select]')) inp.closest('form').submit();
    if (inp.matches('[data-sort-select]')) {
      const url = new URL(location.href); url.searchParams.set('sort_by', inp.value); url.searchParams.delete('page'); location.href = url.toString();
    }
  });

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.addEventListener('change', () => this.update());
    }
    selected() {
      const values = [];
      this.querySelectorAll('fieldset').forEach((fs) => {
        const sel = fs.querySelector('select');
        if (sel) values.push(sel.value); else { const r = fs.querySelector('input:checked'); values.push(r ? r.value : null); }
        const lab = fs.querySelector('[data-selected-value]'); if (lab) lab.textContent = values[values.length - 1] || '';
      });
      return values;
    }
    update() {
      const values = this.selected();
      const v = this.variants.find((x) => x.options.every((o, i) => o === values[i]));
      const section = this.closest('[data-product]');
      const form = section && section.querySelector('form[data-type="add-to-cart-form"]');
      const btn = section && section.querySelector('[data-add-button]');
      const lab = section && section.querySelector('[data-add-label]');
      const priceEl = section && section.querySelector('[data-add-price]');
      const priceBox = section && section.querySelector('.pdp__price .price');
      if (!v) {
        if (btn) btn.setAttribute('disabled', '');
        if (lab) lab.textContent = S.strings.unavailable;
        return;
      }
      if (form) form.querySelector('[name="id"]').value = v.id;
      if (btn) { if (v.available) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', ''); }
      if (lab) lab.textContent = v.available ? S.strings.addToCart : S.strings.soldOut;
      if (priceEl) priceEl.textContent = money(v.price);
      if (priceBox) {
        priceBox.classList.toggle('price--sale', v.compare_at_price > v.price);
        const cur = priceBox.querySelector('.price__current'); if (cur) cur.textContent = money(v.price);
        let cmp = priceBox.querySelector('.price__compare');
        if (v.compare_at_price > v.price) { if (!cmp) { cmp = document.createElement('s'); cmp.className = 'price__compare'; priceBox.appendChild(cmp); } cmp.textContent = money(v.compare_at_price); }
        else if (cmp) cmp.remove();
      }
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url.toString());
      const gallery = section && section.querySelector('product-gallery');
      if (gallery && v.featured_media && gallery.goToMedia) gallery.goToMedia(v.featured_media.id);
    }
  }
  customElements.get('variant-picker') || customElements.define('variant-picker', VariantPicker);

  /* ---------- product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      this.slides = $$('.pdp__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.counter = $('[data-current]', this);
      this.i = 0;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.thumb, 10))));
      const prev = $('[data-prev]', this), next = $('[data-next]', this);
      prev && prev.addEventListener('click', () => this.go(this.i - 1));
      next && next.addEventListener('click', () => this.go(this.i + 1));
      let sx = 0;
      this.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) this.go(this.i + (dx < 0 ? 1 : -1)); }, { passive: true });
      this.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.go(this.i + 1); if (e.key === 'ArrowLeft') this.go(this.i - 1); });
      $$('[data-zoom]', this).forEach((z) => {
        if (!FINE) return;
        z.addEventListener('mousemove', (e) => { const r = z.getBoundingClientRect(); z.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%'); z.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%'); });
        z.addEventListener('click', () => z.classList.toggle('is-zoomed'));
        z.addEventListener('mouseleave', () => z.classList.remove('is-zoomed'));
      });
    }
    go(n) {
      if (!this.slides.length) return;
      this.i = (n + this.slides.length) % this.slides.length;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.i));
      this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === this.i));
      const stage = $('[data-stage]', this), ar = parseFloat(this.slides[this.i].dataset.ar || '1');
      if (stage && ar) stage.style.setProperty('--ar', clamp(ar, 0.62, 1.4));
      if (this.counter) this.counter.textContent = String(this.i + 1).padStart(2, '0');
      const t = this.thumbs[this.i]; if (t && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: RM ? 'auto' : 'smooth' });
    }
    goToMedia(id) { const k = this.slides.findIndex((s) => s.dataset.mediaId === String(id)); if (k > -1) this.go(k); }
  }
  customElements.get('product-gallery') || customElements.define('product-gallery', ProductGallery);

  /* ---------- predictive search ---------- */
  function initSearch() {
    const modal = $('#SearchModal');
    if (!modal || !once(modal, 'init')) return;
    const input = $('[data-search-input]', modal), results = $('[data-search-results]', modal);
    let t, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (q.length < 2) return;
      t = setTimeout(async () => {
        ctrl && ctrl.abort(); ctrl = new AbortController();
        try {
          const url = `${S.routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url, { signal: ctrl.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('.psearch__results');
          if (fresh) results.innerHTML = fresh.outerHTML;
        } catch (e) { /* aborted */ }
      }, 220);
    });
  }

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    const hit = (s) => t.closest(s);
    if (hit('[data-open-menu]')) { e.preventDefault(); openOverlay($('#MobileMenu'), '.mm__link'); }
    else if (hit('[data-close-menu]')) { e.preventDefault(); closeOverlay($('#MobileMenu')); }
    else if (hit('[data-open-search]')) { e.preventDefault(); openOverlay($('#SearchModal'), '[data-search-input]'); }
    else if (hit('[data-close-search]')) { e.preventDefault(); closeOverlay($('#SearchModal')); }
    else if (hit('[data-open-cart]') && drawer()) { e.preventDefault(); openCart(); }
    else if (hit('[data-close-cart]')) { if (!hit('a')) e.preventDefault(); closeOverlay(drawer()); }
    else if (hit('[data-remove]')) { e.preventDefault(); const b = hit('[data-remove]'); changeLine(parseInt(b.dataset.remove, 10), 0, b.closest('.line')); }
    else if (hit('[data-toggle-recover]')) { e.preventDefault(); const l = $('#LoginCard'), r = $('#RecoverCard'); if (l && r) { const show = r.hidden; r.hidden = !show; l.hidden = show; } }
    else if (hit('[data-address-toggle]')) { e.preventDefault(); const b = hit('[data-address-toggle]'); const el = document.getElementById(b.dataset.addressToggle); if (el) { el.hidden = !el.hidden; b.setAttribute('aria-expanded', String(!el.hidden)); } }
    else if (hit('[data-address-delete]')) {
      e.preventDefault(); const b = hit('[data-address-delete]');
      if (confirm(b.dataset.confirm || 'Delete this address?')) {
        const f = document.createElement('form'); f.method = 'post'; f.action = `/account/addresses/${b.dataset.addressDelete}`;
        f.innerHTML = '<input type="hidden" name="_method" value="delete">'; body.appendChild(f); f.submit();
      }
    }
    else if (hit('[data-share]')) {
      e.preventDefault(); const b = hit('[data-share]');
      if (navigator.share) navigator.share({ title: b.dataset.shareTitle, url: b.dataset.shareUrl }).catch(() => {});
      else navigator.clipboard && navigator.clipboard.writeText(b.dataset.shareUrl).then(() => toast('Link copied'));
    }
    else {
      const a = hit('a[href^="#"]');
      if (a && a.getAttribute('href').length > 1) { const target = document.getElementById(a.getAttribute('href').slice(1)); if (target) { e.preventDefault(); scrollToEl(target); } }
    }
  });

  /* ---------- page transitions ---------- */
  function initTransitions() {
    const veil = $('.page-veil');
    if (!veil || !flag('transitions') || RM || S.designMode) return;
    if (html.classList.contains('is-arriving')) {
      try { sessionStorage.removeItem('cs:t'); } catch (e) { /* no storage */ }
      requestAnimationFrame(() => requestAnimationFrame(() => html.classList.add('is-arrived')));
      setTimeout(() => html.classList.remove('is-arriving', 'is-arrived'), 1200);
    }
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || url.pathname === location.pathname && url.hash) return;
      if (/\/(cart|checkout|account\/logout|admin)/.test(url.pathname)) return;
      e.preventDefault();
      try { sessionStorage.setItem('cs:t', '1'); } catch (err) { /* ignore */ }
      veil.classList.add('is-active');
      setTimeout(() => { location.href = url.href; }, 640);
    });
    addEventListener('pageshow', (e) => { if (e.persisted) { veil.classList.remove('is-active'); html.classList.remove('is-arriving', 'is-arrived'); } });
  }

  /* ---------- preloader ---------- */
  function preloader(done) {
    const pre = $('#Preloader');
    let seen = false;
    try { seen = !S.preloaderEveryVisit && sessionStorage.getItem('cs:pre') === '1'; } catch (e) { /* no storage */ }
    if (!pre || seen || RM || S.designMode || html.classList.contains('is-arriving')) { if (pre) pre.classList.add('is-skipped'); done(); return; }
    try { sessionStorage.setItem('cs:pre', '1'); } catch (e) { /* ignore */ }
    lock();
    const num = $('.preloader__count b', pre), bar = $('.preloader__panel', pre);
    const words = $$('[data-pre-words] i', pre);
    let wi = 0;
    const wt = words.length > 1 ? setInterval(() => { words[wi].classList.remove('is-on'); wi = (wi + 1) % words.length; words[wi].classList.add('is-on'); }, 150) : 0;
    const t0 = performance.now(), dur = 1700;
    let finished = false;
    const finish = () => { if (finished) return; finished = true; clearInterval(wt); if (words.length) { words.forEach((w) => w.classList.remove('is-on')); words[0].classList.add('is-on'); } pre.classList.add('is-done'); body.classList.remove('is-locked'); setTimeout(done, 250); };
    const step = (t) => {
      const p = clamp((t - t0) / dur, 0, 1);
      const e = EASE_OUT(p);
      if (num) num.textContent = Math.round(e * 100);
      if (bar) bar.style.setProperty('--p', e.toFixed(3));
      if (p < 1) requestAnimationFrame(step); else setTimeout(finish, 180);
    };
    requestAnimationFrame(step);
    setTimeout(finish, 3200); // hard fallback
  }

  /* ---------- address country/province ---------- */
  function initAddresses(root) {
    if (!window.Shopify || !window.Shopify.CountryProvinceSelector) return;
    $$('[data-address-country-select]', root).forEach((sel) => {
      if (!once(sel, 'init')) return;
      const pid = sel.dataset.addressProvinceId;
      new window.Shopify.CountryProvinceSelector(sel.id, pid, { hideElement: pid.replace('AddressProvince', 'AddressProvinceContainer') });
    });
  }

  /* ---------- product recommendations ---------- */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (!once(el, 'init') || !el.dataset.url) return;
      try {
        const res = await fetch(el.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; observe(el); }
      } catch (e) { /* silent */ }
    });
  }


  /* =========================================================
     Character Select
     ========================================================= */

  /* ---------- the blank: one word visible, rolls to the next ---------- */
  function setBlank(el, idx) {
    const words = $$('[data-blank-word]', el);
    if (!words.length) return;
    const n = ((idx % words.length) + words.length) % words.length;
    const cur = words.findIndex((w) => w.classList.contains('is-on'));
    if (cur === n) return;
    words.forEach((w, i) => {
      w.classList.remove('is-out');
      if (i === cur) { w.classList.remove('is-on'); w.classList.add('is-out'); }
      if (i === n) w.classList.add('is-on');
      w.setAttribute('aria-hidden', String(i !== n));
    });
  }
  function initBlank(root) {
    $$('[data-blank-cycle]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const words = $$('[data-blank-word]', el);
      if (words.length < 2 || RM) return;
      const ms = Math.max(600, parseInt(el.dataset.blankCycle, 10) || 1400);
      let i = 0, vis = false;
      new IntersectionObserver((es) => { vis = es[0].isIntersecting; }).observe(el);
      setInterval(() => { if (!vis || document.hidden) return; i++; setBlank(el, i); }, ms);
    });
  }

  /* ---------- character select hero ---------- */
  function initSelect(root) {
    $$('[data-select]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const tiles = $$('[data-tile]', el), figs = $$('[data-fig]', el), infos = $$('[data-info]', el);
      const blank = $('[data-blank]', el), frame = $('[data-frame]', el), roster = $('[data-roster]', el);
      const stage = $('[data-stage]', el), flipBtn = $('[data-flip]', el), flipLab = $('[data-flip-label]', el);
      const timer = $('[data-timer]', el), timerBox = timer && timer.parentElement;
      const n = tiles.length;
      if (!n) return;
      let cur = 0, ry = 0, auto = parseInt(el.dataset.autoplay || '0', 10) * 1000, autoT, stopped = RM || !auto, inView = true;

      const placeFrame = () => {
        const t = tiles[cur]; if (!frame || !t) return;
        frame.style.setProperty('--fw', t.offsetWidth + 'px');
        frame.style.setProperty('--fh', t.offsetHeight + 'px');
        frame.style.setProperty('--fx0', t.offsetLeft + 'px');
        frame.style.setProperty('--fy0', t.offsetTop + 'px');
      };
      const setTurn = (deg, animate = true) => {
        ry = deg;
        figs.forEach((f) => { const t = $('[data-turn]', f); if (t) t.style.setProperty('--ry', deg + 'deg'); });
        const back = Math.abs(((deg % 360) + 360) % 360 - 180) < 90;
        if (flipBtn) flipBtn.setAttribute('aria-pressed', String(back));
        if (flipLab) flipLab.textContent = back ? flipLab.dataset.back : flipLab.dataset.front;
      };
      const go = (i, user) => {
        i = ((i % n) + n) % n;
        if (user) stop();
        if (i === cur) return;
        const dir = i > cur ? 1 : -1;
        figs.forEach((f, k) => {
          f.style.setProperty('--from', (dir * 9) + '%');
          f.style.setProperty('--to', (-dir * 9) + '%');
          f.classList.toggle('is-out', k === cur);
          f.classList.toggle('is-on', k === i);
        });
        setTimeout(() => figs.forEach((f, k) => { if (k !== i) f.classList.remove('is-out'); }), 650);
        infos.forEach((inf, k) => {
          inf.classList.toggle('is-on', k === i);
          const a = $('[data-select-go]', inf); if (a) a.tabIndex = k === i ? 0 : -1;
        });
        tiles.forEach((t, k) => { t.classList.toggle('is-on', k === i); t.setAttribute('aria-selected', String(k === i)); });
        cur = i;
        if (ry % 360 !== 0) setTurn(0);
        if (blank) setBlank(blank, i);
        placeFrame();
        if (!stopped) schedule();
      };
      const schedule = () => {
        clearTimeout(autoT);
        if (stopped) return;
        if (timerBox) { timerBox.classList.remove('is-running'); void timerBox.offsetWidth; timerBox.style.setProperty('--t', auto + 'ms'); timerBox.classList.add('is-running'); }
        autoT = setTimeout(() => { if (inView && !document.hidden) go(cur + 1); else schedule(); }, auto);
      };
      const stop = () => { if (stopped) return; stopped = true; clearTimeout(autoT); if (timerBox) { timerBox.classList.remove('is-running'); timerBox.classList.add('is-stopped'); } };

      tiles.forEach((t, k) => {
        t.addEventListener('click', () => { if (k === cur) { const a = $('[data-select-go]', infos[k]); if (a && el.dataset.tapped === String(k)) { location.href = a.href; return; } } el.dataset.tapped = String(k); go(k, true); });
        if (FINE) t.addEventListener('mouseenter', () => go(k, true));
      });
      if (flipBtn) flipBtn.addEventListener('click', (e) => { e.stopPropagation(); stop(); setTurn(ry + 180); });

      // drag to turn the piece
      if (stage) {
        let sx = 0, base = 0, dragging = false, moved = false;
        stage.addEventListener('pointerdown', (e) => {
          if (e.target.closest('[data-flip]')) return;
          dragging = true; moved = false; sx = e.clientX; base = ry;
          stage.setPointerCapture(e.pointerId);
        });
        stage.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const dx = e.clientX - sx;
          if (Math.abs(dx) > 4) { moved = true; stage.classList.add('is-dragging'); stop(); }
          if (moved) setTurn(base + dx * 0.6, false);
        });
        const end = () => {
          if (!dragging) return; dragging = false; stage.classList.remove('is-dragging');
          if (moved) setTurn(Math.round(ry / 180) * 180);
          else { stop(); setTurn(ry + 180); }
        };
        stage.addEventListener('pointerup', end);
        stage.addEventListener('pointercancel', end);
      }

      // keyboard: ← → move, ↑ ↓ jump a row, Enter opens
      document.addEventListener('keydown', (e) => {
        if (!inView || e.target.closest('input, textarea, select, [contenteditable]') || body.classList.contains('is-locked')) return;
        const cols = roster ? getComputedStyle(roster).gridTemplateColumns.split(' ').length : 3;
        const map = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols };
        if (e.key in map) { e.preventDefault(); go(cur + map[e.key], true); tiles[cur].focus({ preventScroll: true }); }
        else if (e.key === 'Enter' && document.activeElement && document.activeElement.matches('[data-tile]')) { const a = $('[data-select-go]', infos[cur]); if (a) location.href = a.href; }
      });

      new IntersectionObserver((es) => { inView = es[0].intersectionRatio > 0.25; }, { threshold: [0, 0.25, 0.5] }).observe(el);
      resizers.push(placeFrame);
      placeFrame(); setTimeout(placeFrame, 400); if (document.fonts) document.fonts.ready.then(placeFrame);
      if (!stopped) schedule();
    });
  }

  /* ---------- colourway filters (FLIP) ---------- */
  function initFilter(root) {
    $$('[data-filter-root]', root).forEach((el) => {
      if (!once(el, 'filterInit')) return;
      const chips = $$('[data-filter]', el), grid = $('[data-filter-grid]', el);
      if (!chips.length || !grid) return;
      chips.forEach((c) => c.addEventListener('click', () => {
        const f = c.dataset.filter;
        chips.forEach((x) => { x.classList.toggle('is-on', x === c); x.setAttribute('aria-pressed', String(x === c)); });
        const cards = $$('.cs-card', grid);
        const first = new Map(cards.map((k) => [k, k.getBoundingClientRect()]));
        cards.forEach((k) => k.classList.toggle('is-hidden', f !== '*' && k.dataset.colour !== f));
        if (RM) return;
        cards.forEach((k) => {
          if (k.classList.contains('is-hidden')) return;
          const a = first.get(k), b = k.getBoundingClientRect();
          k.classList.add('in');
          if (!a.width) { k.animate([{ opacity: 0, transform: 'scale(.94)' }, { opacity: 1, transform: 'none' }], { duration: 500, easing: 'cubic-bezier(.19,1,.22,1)' }); return; }
          const dx = a.left - b.left, dy = a.top - b.top;
          if (dx || dy) k.animate([{ transform: `translate(${dx}px,${dy}px)` }, { transform: 'none' }], { duration: 650, easing: 'cubic-bezier(.19,1,.22,1)' });
        });
      }));
    });
  }

  /* ---------- in the wild: drag + buttons + progress ---------- */
  function initWild(root) {
    $$('[data-wild]', root).forEach((rail) => {
      if (!once(rail, 'init')) return;
      const sec = rail.closest('.wild'), bar = sec && $('[data-wild-bar]', sec);
      const pin = sec && $('.wild__pin', sec), track = $('.wild__track', rail);
      const canPin = !!(sec && pin && track && sec.dataset.pin === 'true' && !RM);
      let pinned = false, dist = 0, last = -1;
      const step = () => { const it = $('.wild__item', rail); return it ? it.offsetWidth + parseFloat(getComputedStyle(track).columnGap || 24) : 300; };
      const setBar = (p) => { if (bar) bar.style.setProperty('--p', Math.max(.08, p).toFixed(3)); };
      const upd = () => { if (pinned) return; const max = rail.scrollWidth - rail.clientWidth; setBar(max > 0 ? rail.scrollLeft / max : 1); };
      /* pinned: the section is as tall as the sideways distance, the pin sticks, scroll position → track position */
      const layout = () => {
        const want = canPin && innerHeight >= 480;
        if (want !== pinned) {
          pinned = want;
          sec.classList.toggle('is-pinned', pinned);
          if (pinned) rail.removeAttribute('data-native-scroll'); else rail.setAttribute('data-native-scroll', '');
          track.style.transform = ''; rail.scrollLeft = 0; last = -1;
        }
        if (pinned) {
          dist = Math.max(0, track.offsetWidth - rail.clientWidth);
          sec.style.height = (pin.offsetHeight + dist) + 'px';
          last = -1;
        } else { sec.style.height = ''; upd(); }
      };
      const progress = () => { const r = sec.getBoundingClientRect(); return { r, p: dist > 0 ? clamp(-r.top / dist, 0, 1) : 0 }; };
      if (canPin) {
        loops.push(() => {
          if (!pinned) return;
          const { r, p } = progress();
          if (r.bottom < -60 || r.top > H + 60) return;
          if (Math.abs(p - last) < 0.0004) return;
          last = p;
          track.style.transform = `translate3d(${(-p * dist).toFixed(1)}px,0,0)`;
          setBar(p);
        });
        /* keyboard focus on a photo that is off to the side: scroll the page to it */
        sec.addEventListener('focusin', (e) => {
          const it = pinned && e.target.closest && e.target.closest('.wild__item');
          if (!it) return;
          const r = sec.getBoundingClientRect();
          const x = clamp(it.offsetLeft - (rail.clientWidth - it.offsetWidth) / 2, 0, dist);
          window.scrollTo(0, window.scrollY + r.top + x);
        });
        $$('img', rail).forEach((im) => { if (!im.complete) im.addEventListener('load', layout, { once: true }); });
      }
      layout(); resizers.push(layout);
      rail.addEventListener('scroll', upd, { passive: true });
      const go = (dir) => {
        if (!pinned) { rail.scrollBy({ left: dir * step(), behavior: RM ? 'auto' : 'smooth' }); return; }
        const r = sec.getBoundingClientRect(), s = step();
        const x = clamp((Math.round(clamp(-r.top, 0, dist) / s) + dir) * s, 0, dist);
        const top = window.scrollY + r.top + x;
        if (smooth && window.CS.scrollTo) window.CS.scrollTo(top); else window.scrollTo({ top, behavior: 'smooth' });
      };
      if (sec) {
        const prev = $('[data-wild-prev]', sec), next = $('[data-wild-next]', sec);
        prev && prev.addEventListener('click', () => go(-1));
        next && next.addEventListener('click', () => go(1));
      }
      if (!FINE) return;
      /* mouse drag: moves the rail (pinned: by scrolling the page) */
      let down = false, sx = 0, sl = 0, sy = 0, v = 0, lx = 0, moved = false;
      rail.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; down = true; moved = false; sx = lx = e.clientX; sl = rail.scrollLeft; sy = window.scrollY; v = 0; });
      addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 5) { moved = true; rail.classList.add('is-dragging'); }
        v = e.clientX - lx; lx = e.clientX;
        if (pinned) window.scrollTo(0, sy - dx); else rail.scrollLeft = sl - dx;
      });
      addEventListener('pointerup', () => {
        if (!down) return; down = false;
        setTimeout(() => rail.classList.remove('is-dragging'), 0);
        if (!moved || RM) return;
        const glide = () => { v *= 0.93; if (pinned) window.scrollBy(0, -v); else rail.scrollLeft -= v; if (Math.abs(v) > 0.4) requestAnimationFrame(glide); };
        requestAnimationFrame(glide);
      });
      rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    });
  }

  /* ---------- photo tiles take the photo's own background colour ---------- */
  const tileCache = new Map();
  function tileColour(src, cb) {
    if (tileCache.has(src)) { cb(tileCache.get(src)); return; }
    let url = src;
    try {
      const u = new URL(src, location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) { u.searchParams.set('width', '24'); url = u.href; }
    } catch (e) { /* keep src */ }
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      try {
        const c = document.createElement('canvas'); c.width = 8; c.height = 8;
        const x = c.getContext('2d'); x.drawImage(im, 0, 0, 8, 8);
        const d = x.getImageData(0, 0, 1, 1).data;
        const col = d[3] > 250 ? `rgb(${d[0]},${d[1]},${d[2]})` : null;
        tileCache.set(src, col); cb(col);
      } catch (err) { tileCache.set(src, null); }
    };
    im.src = url;
  }
  function initTiles(root) {
    if (getComputedStyle(document.documentElement).getPropertyValue('--card-blend').trim() !== 'multiply') return; // setting off: photos keep their own background
    $$('.cs-card__media, .pdp__stage, .pdp__thumb, .line__media, .is-photo', root).forEach((box) => {
      if (!once(box, 'tileBg')) return;
      const img = $('img', box);
      if (!img) return;
      // the tile is the page cream; a light, neutral photo background (the mockups' #F0F0F0) is lifted to white
      // and multiplied into the cream, so the shirt sits straight on the page with its colours unchanged
      const run = () => tileColour(img.currentSrc || img.src, (col) => {
        const c = col && col.match(/\d+/g).map(Number);
        if (!c) return;
        const hi = Math.max(...c), lo = Math.min(...c);
        if (lo < 200 || hi - lo > 14) return;
        box.style.setProperty('--knock', Math.min(1.2, 255 / hi).toFixed(4));
        box.classList.add('is-knocked');
      });
      if (img.complete && img.naturalWidth) run(); else img.addEventListener('load', run, { once: true });
    });
  }


  /* ---------- the sentence lights up word by word on scroll ---------- */
  function initScrollLight(root) {
    $$('[data-scroll-light]', root).forEach((el) => {
      if (!once(el, 'lightInit')) return;
      const units = $$('.w, .cs-blank', el);
      if (!units.length) return;
      if (RM || !revealsOn) { units.forEach((u) => u.classList.add('lit')); return; }
      el.classList.add('is-light');
      loops.push(() => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -50 || r.top > H + 50) return;
        const start = H * 0.88, end = H * 0.3;
        const p = clamp((start - r.top) / (start - end + r.height * 0.4), 0, 1);
        const n = Math.round(p * units.length);
        units.forEach((u, i) => u.classList.toggle('lit', i < n));
      });
    });
  }

  /* ---------- hero stage leans toward the pointer (desktop) / the phone tilt (touch) ---------- */
  function initLean(root) {
    $$('[data-select]', root).forEach((el) => {
      if (!once(el, 'leanInit') || RM) return;
      const figs = $('.cs-sel__figs', el);
      if (!figs) return;
      let tx = 0, ty = 0, cx = 0, cy = 0;
      if (FINE) {
        el.addEventListener('mousemove', (e) => {
          const r = figs.getBoundingClientRect();
          tx = clamp((e.clientX - (r.left + r.width / 2)) / r.width, -1, 1);
          ty = clamp((e.clientY - (r.top + r.height / 2)) / r.height, -1, 1);
        });
        el.addEventListener('mouseleave', () => { tx = ty = 0; });
      } else {
        // phones: a slow idle sway so the piece never sits still
        loops.push((t) => { tx = Math.sin(t / 1600) * 0.5; ty = Math.cos(t / 2100) * 0.3; });
      }
      loops.push(() => {
        cx = lerp(cx, tx, 0.08); cy = lerp(cy, ty, 0.08);
        if (Math.abs(cx) < 0.0005 && Math.abs(cy) < 0.0005) return;
        figs.style.transform = `perspective(1400px) rotateY(${(cx * 7).toFixed(2)}deg) rotateX(${(-cy * 5).toFixed(2)}deg)`;
      });
    });
  }

  /* ---------- the street-photo rail drifts with the page scroll ---------- */
  function initDrift(root) {
    $$('[data-wild]', root).forEach((rail) => {
      if (!once(rail, 'driftInit') || RM) return;
      const track = $('.wild__track', rail), sec = rail.closest('.wild');
      loops.push(() => {
        if (sec && sec.classList.contains('is-pinned')) return; // the pin moves the track itself
        const r = rail.getBoundingClientRect();
        if (r.bottom < 0 || r.top > H) return;
        const p = (r.top + r.height / 2 - H / 2) / H; // -0.5 … 0.5 while crossing
        track.style.transform = `translate3d(${(p * 90).toFixed(1)}px,0,0)`;
      });
    });
  }

  /* =========================================================
     Boot
     ========================================================= */
  /* ---------- size guide: Tapstitch's size table from the product description, as a branded side panel ---------- */
  function readSizeTable(table) {
    // row 1: blank | S (colspan 2) | M … ; row 2: inch | cm | inch | cm … ; then one row per measurement
    const rows = [...table.rows];
    if (rows.length < 3) return null;
    const sizes = [];
    [...rows[0].cells].slice(1).forEach((c) => { const n = c.textContent.trim(); for (let i = 0; i < (c.colSpan || 1); i++) sizes.push(n); });
    const units = [...rows[1].cells].map((c) => c.textContent.trim().toLowerCase()).filter(Boolean);
    if (!sizes.length || units.length !== sizes.length) return null;
    const order = [...new Set(sizes)];
    const measures = rows.slice(2).map((r) => {
      const cells = [...r.cells].map((c) => c.textContent.trim());
      const vals = {}; cells.slice(1).forEach((v, i) => { const u = /cm/.test(units[i]) ? 'cm' : 'inch'; (vals[sizes[i]] = vals[sizes[i]] || {})[u] = v; });
      return { label: cells[0], vals };
    }).filter((m) => m.label);
    return measures.length ? { sizes: order, measures } : null;
  }
  function initSizeGuide(root) {
    $$('[data-sizeguide]', root).forEach((sg) => {
      if (!once(sg, 'sgInit')) return;
      const src = $('template[data-sg-source]', sg);
      const table = src && src.content.querySelector('table');
      const data = table && readSizeTable(table);
      const section = sg.closest('.shopify-section') || document;
      if (!data) { $$('[data-open-sizeguide]', section).forEach((b) => b.remove()); return; }
      const box = $('[data-sg-table]', sg);
      let unit = 'cm';
      try { unit = localStorage.getItem('cs:sg-unit') || 'cm'; } catch (e) { /* storage off */ }
      const selectedSize = () => {
        const fs = $$('variant-picker fieldset', section).find((f) => /size|größe|taille/i.test(($('legend', f) || {}).textContent || ''));
        const r = fs && $('input:checked', fs); const sel = fs && $('select', fs);
        return ((r && r.value) || (sel && sel.value) || '').trim().toUpperCase();
      };
      const render = () => {
        const cur = selectedSize();
        const head = `<tr><th scope="col">${sg.dataset.tSize || 'Size'}</th>${data.measures.map((m) => `<th scope="col">${m.label}</th>`).join('')}</tr>`;
        const body = data.sizes.map((z) => `<tr${z.toUpperCase() === cur ? ' class="is-on"' : ''}><th scope="row">${z}</th>${data.measures.map((m) => `<td>${(m.vals[z] && m.vals[z][unit]) || '–'}</td>`).join('')}</tr>`).join('');
        box.innerHTML = `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
        $$('[data-sg-unit]', sg).forEach((t) => { const on = t.dataset.sgUnit === unit; t.classList.toggle('is-on', on); t.setAttribute('aria-selected', String(on)); });
      };
      render();
      sg.addEventListener('click', (e) => {
        const t = e.target.closest('[data-sg-unit]');
        if (t) { unit = t.dataset.sgUnit; try { localStorage.setItem('cs:sg-unit', unit); } catch (err) { /* storage off */ } render(); }
        if (e.target.closest('[data-close-sizeguide]')) closeOverlay(sg);
      });
      $$('[data-open-sizeguide]', section).forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); render(); openOverlay(sg, '[data-sg-unit].is-on'); }));
      // the cramped table in the description becomes a link to the guide
      $$('.acc__body table, .pdp__desc table', section).forEach((tb) => {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'pdp__sg-inline'; b.textContent = sg.dataset.tSee || 'See the size guide';
        b.addEventListener('click', () => { render(); openOverlay(sg, '[data-sg-unit].is-on'); });
        tb.replaceWith(b);
      });
    });
  }

  function initRoot(root = document) {
    observe(root);
    initAnnouncement(root);
    initMarquee(root);
    initParallax(root);
    initBlank(root);
    initSelect(root);
    initFilter(root);
    initWild(root);
    initTiles(root);
    initScrollLight(root);
    initLean(root);
    initDrift(root);
    initAddresses(root);
    initRecommendations(root);
    initSizeGuide(root);
    if (window.CS.collectMags) window.CS.collectMags(root);
  }

  function boot() {
    initHeader();
    initPointer();
    initSearch();
    initTransitions();
    requestAnimationFrame(frame);
    window.CS.initRoot = initRoot;
    preloader(() => { initRoot(document); requestAnimationFrame(() => body.classList.add('is-ready')); });
    if (S.designMode) {
      document.addEventListener('shopify:section:load', (e) => { initRoot(e.target); });
      document.addEventListener('shopify:section:select', (e) => { if (e.target.querySelector('#CartDrawer')) openOverlay(drawer()); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.querySelector('#CartDrawer')) closeOverlay(drawer()); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
